/**
 * Where Instagram sends the creator back after they authorise.
 *
 * Unlike the Facebook SDK's popup, the direct Instagram flow is a full-page
 * redirect, so the sign-in finishes on its own route. This page exists only
 * to swap the `?code=` for a session and get out of the way.
 */

import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { IG_LOGIN_REDIRECT_URI } from '../../config';
import { authHandlers } from '../../api_handlers/auth';

const CSS = `
.ig-cb { min-height: 70vh; display: grid; place-items: center; padding: 40px 20px; text-align: center; }
.ig-cb__inner { max-width: 420px; }
.ig-cb__spin {
  width: 30px; height: 30px; margin: 0 auto 18px; border-radius: 50%;
  border: 3px solid #EADCF4; border-top-color: #C13584;
  animation: ig-cb-spin .8s linear infinite;
}
@keyframes ig-cb-spin { to { transform: rotate(360deg); } }
@media (prefers-reduced-motion: reduce) { .ig-cb__spin { animation: none; } }
.ig-cb__title { font-size: 18px; font-weight: 700; color: #111936; margin-bottom: 8px; }
.ig-cb__text { font-size: 14px; color: #5b6a67; line-height: 1.55; }
.ig-cb__btn {
  margin-top: 18px; display: inline-block; font-size: 14px; font-weight: 700;
  color: #fff; background: #C13584; border: 0; border-radius: 8px;
  padding: 10px 18px; cursor: pointer; text-decoration: none;
}
`;

export default function InstagramCallback() {
  const history = useHistory();
  const location = useLocation();
  const [error, setError] = useState(null);
  // Instagram authorisation codes are single-use. React 18 StrictMode (and
  // any re-render) would fire this twice and the second redemption fails,
  // which looked exactly like a rejected login.
  const claimed = useRef(false);

  useEffect(() => {
    if (claimed.current) return;
    claimed.current = true;

    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const denied = params.get('error');

    if (denied) {
      setError(
        params.get('error_description') ||
          'You cancelled the Instagram sign-in. Nothing was connected.',
      );
      return;
    }
    if (!code) {
      setError('Instagram did not send an authorisation code. Please try again.');
      return;
    }

    authHandlers.instagramLogin(code, IG_LOGIN_REDIRECT_URI).then(
      (user) => {
        const destination =
          user && user.created ? '/influencer/profile-setup' : '/influencer/campaigns';
        history.replace(destination);
      },
      (e) => setError(e.message || 'Instagram sign-in failed. Please try again.'),
    );
  }, [history, location.search]);

  return (
    <div className="iv-root">
      <style>{CSS}</style>
      <div className="ig-cb">
        <div className="ig-cb__inner">
          {error ? (
            <>
              <p className="ig-cb__title">We couldn&apos;t finish signing you in</p>
              <p className="ig-cb__text">{error}</p>
              <button
                type="button"
                className="ig-cb__btn"
                onClick={() => history.replace('/influencer-login')}
              >
                Try again
              </button>
            </>
          ) : (
            <>
              <span className="ig-cb__spin" aria-hidden="true" />
              <p className="ig-cb__title">Finishing your Instagram sign-in…</p>
              <p className="ig-cb__text">This only takes a moment.</p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
